import { type FC, type HTMLAttributes, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { type InjectedFormProps, SubmissionError, formValueSelector, reduxForm } from "redux-form";

import { Dialog, TriggerButton, useDialog, useMultilineText } from "widgets";

import InputField, { InputReduxFormField } from "../core/redux-form/InputField.js";
import TextareaDropzone, {
  type DroppedFile,
  TextareaDropzoneReduxFormField,
} from "../core/redux-form/TextareaDropzone.js";
import { type IBlastInputFormProps } from "../playbooks/blast/BlastInputForm.js";
import type { IUploadFileResponse } from "@/api/WebApiClient.ts";
import { createErrorHandler } from "@/app-utils/errors.ts";
import { getFormError, simpleFieldValidation } from "@/app-utils/form.ts";
import { type Dictionary } from "@/app-utils/type.ts";
import type { InputCoordinateListInput, Organism, Project } from "@/graphql.ts";
import { SelMessage } from "@/messages/SelMessage.tsx";
import { selMessage } from "@/messages/message.ts";
import { notify } from "@/modules/notifications/notify.js";
import { loadUserParams } from "@/redux/modules/auth.ts";
import {
  type IFileUpload,
  abortFileUpload,
  anyFileHasStatus,
  pendingUploadOrValidation,
  resetAllFileUploads,
  uploadFile,
} from "@/redux/modules/file-upload.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxAction, type ReduxDispatch } from "@/redux/redux-utils.ts";
import { FILE_STATUS, MAX_LENGTH } from "@/settings/settings.ts";
import ProjectsSelect from "@/ui-modules/core/forms/ProjectsSelect.tsx";
import { ProjectTagInputReduxFormField } from "@/ui-modules/core/redux-form/ProjectTagInputField.tsx";
import { SequencedGenomeSelector } from "@/ui-modules/files/SequencedGenomeSelector.tsx";

interface CoordinateListUploadButtonProps {
  defaultProjectId?: number;
  defaultOrganismId?: number;
  onUploadSuceeded?: () => void;
  showLabel?: boolean;
}

export const CoordinateListUploadButton: FC<CoordinateListUploadButtonProps> = (props) => {
  const { dialogProps, underlayProps, state } = useDialog<HTMLDivElement>();

  return (
    <>
      <TriggerButton onPress={state.open} icon={"cloud_upload"} label={"Upload List"} showLabel={props.showLabel} />
      {state.isOpen && (
        <CoordinatesListUploadManager
          key="coord-lists-upload-form-dialog"
          close={state.close}
          dialogProps={dialogProps}
          underlayProps={underlayProps}
          {...props}
        />
      )}
    </>
  );
};

export interface UploadDialogFormProps extends CoordinateListUploadButtonProps {
  fileUploads: Dictionary<IFileUpload>;
  dialogProps: HTMLAttributes<HTMLElement>;
  underlayProps: HTMLAttributes<HTMLElement>;
  close: () => void;
}

interface UploadDialogProps extends UploadDialogFormProps, InjectedFormProps<FormData, any> {}

const COORD_LIST_UPLOAD_FORM_NAME = "COORD_LIST_UPLOAD_FORM_NAME";
const COORD_LIST_UPLOAD_FORM_KEYS = {
  PROJECT: "project",
  NAME: "name",
  DESCRIPTION: "description",
  FILE: "file",
  GENOME: "genome",
  TAGS: "tags",
};

const UploadDialog: FC<UploadDialogProps> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const multilineError = useMultilineText(props.error);

  const selectedProject = useSelector<IAppState, Project>((state) =>
    formValueSelector(COORD_LIST_UPLOAD_FORM_NAME)(state, COORD_LIST_UPLOAD_FORM_KEYS.PROJECT),
  );

  const handleSubmitData = useCallback(
    (values, _dispatch, props: UploadDialogProps) => {
      const uploadInput = createCoordinatesListUploadInput(
        values,
        props.fileUploads,
        props.defaultOrganismId,
        props.defaultProjectId,
      );

      return dispatch(submitCoordinateListUpload(uploadInput)).then(
        (_fulfilled) => {
          // Reload current lastSaveTarget from configuration
          props.close();
          props.onUploadSuceeded?.();
          void dispatch(loadUserParams());
        },
        // do nothing, the original "submitBlastJob" already executed the business logic regarding the success case
        // the following will throw an error that redux-form understands as a submission error, with a general error that will be
        // available from this.props.error
        (rejected) => {
          throw new SubmissionError({ _error: rejected.message });
        },
      );
    },
    [props.fileUploads, props.close, props.defaultOrganismId, props.defaultProjectId],
  );

  const globalFormError = useSelector<IAppState, string>((state) => getFormError(COORD_LIST_UPLOAD_FORM_NAME, state));

  // directly after the upload we set the status of the file to "validation waiting" ; if upstream validation failed then this will stay in the status until the previous validation is resolved
  const uploadCallback = useCallback((file: File, formData: FormData) => {
    void dispatch(uploadFile(file, formData)).then((response: IUploadFileResponse) => {
      if (response) {
        // only in case of success
        //dispatch({ type: actionTypes.FILE_VALIDATION_WAITING, payload: [response] });
      }
    });
  }, []);

  return (
    <Dialog
      title={"Upload Coordinate List"}
      mainButton={{
        label: "Save",
        onPress: props.handleSubmit(handleSubmitData),
        isLoading: !!(props.submitting || pendingUploadOrValidation(props.fileUploads)),
      }}
      errorCallout={{ message: globalFormError || multilineError }}
      closeFn={props.close}
      domProps={props.dialogProps}
      underlayProps={props.underlayProps}
    >
      <div className="form-group">
        <InputReduxFormField
          label={"List name"}
          placeholder={"Enter coordinate list name"}
          componentClass="input"
          name={COORD_LIST_UPLOAD_FORM_KEYS.NAME}
          maxLength={MAX_LENGTH.COORD_LIST.NAME}
          component={InputField}
          validate={simpleFieldValidation}
        />

        <InputReduxFormField
          label={"Description"}
          placeholder={"Enter coordinate list description"}
          componentClass="textarea"
          name={COORD_LIST_UPLOAD_FORM_KEYS.DESCRIPTION}
          maxLength={MAX_LENGTH.COORD_LIST.DESCRIPTION}
          component={InputField}
        />

        {!props.defaultProjectId && (
          <ProjectsSelect
            name={COORD_LIST_UPLOAD_FORM_KEYS.PROJECT}
            title={"Save to"}
            tooltip="Select a destination"
            onInitToLastTarget={props.change}
          />
        )}

        <ProjectTagInputReduxFormField
          name={COORD_LIST_UPLOAD_FORM_KEYS.TAGS}
          label="Tags"
          projectId={selectedProject?.id || props.defaultProjectId}
        />

        {!props.defaultOrganismId && (
          <SequencedGenomeSelector
            fieldName={COORD_LIST_UPLOAD_FORM_KEYS.GENOME}
            name={"Genome"}
            submitting={props.submitting}
            clearable={true}
            multiple={false}
            validate={simpleFieldValidation}
          />
        )}

        <TextareaDropzoneReduxFormField
          name={COORD_LIST_UPLOAD_FORM_KEYS.FILE}
          title={`BED File`}
          component={TextareaDropzone}
          horizontalLayout={false}
          validate={validateBedFileField}
          handleUploadFile={uploadCallback}
          abortFileupload={(fileName: string) => dispatch(abortFileUpload(fileName))}
          fileUploads={props.fileUploads}
          onlyFiles
          singleFile
          accept={{ "text/bed": [".bed"] }}
          dropzonePlaceholderMessage={"Drop .bed file here to upload"}
        />
      </div>
    </Dialog>
  );
};

function submitCoordinateListUpload(
  coordListUploadInput: CoordinateListUploadInputProps,
): ReduxAction<Promise<boolean>> {
  return (dispatch, _getState, webApiClient) => {
    return webApiClient
      .submitCoordinatesListUpload(coordListUploadInput)
      .then((result: boolean) => {
        notify.info({
          title: selMessage({ id: "coordList.upload.successTitle" }),
          children: SelMessage({
            id: "coordList.upload.successMessage",
          }),
        });

        return result;
      })
      .catch(
        createErrorHandler(dispatch, {
          showAlert: false,
          throwError: true,
        }),
      );
  };
}

const createCoordinatesListUploadInput = (
  values,
  fileUploads: Dictionary<IFileUpload>,
  defaultOrganismId: number,
  defaultProjectId: number,
): InputCoordinateListInput => {
  const name: string = values[COORD_LIST_UPLOAD_FORM_KEYS.NAME];
  const description: string = values[COORD_LIST_UPLOAD_FORM_KEYS.DESCRIPTION];
  const project: Project = values[COORD_LIST_UPLOAD_FORM_KEYS.PROJECT];
  const genome: Organism = values[COORD_LIST_UPLOAD_FORM_KEYS.GENOME];
  const files: DroppedFile[] = values[COORD_LIST_UPLOAD_FORM_KEYS.FILE];
  const tagIds: number[] = values[COORD_LIST_UPLOAD_FORM_KEYS.TAGS];
  // there should be exactly one single file in the drop zone by restriction/validation
  const bedFileUploadId: string = fileUploads[files[0].name].file.id;

  return {
    name,
    description,
    projectId: project?.id || defaultProjectId,
    genomeId: genome?.id || defaultOrganismId,
    itemTagIds: tagIds,
    fileId: bedFileUploadId,
  } satisfies InputCoordinateListInput;
};

const validateBedFileField = (value) => {
  if (!Array.isArray(value) || !value.length) {
    return "A single .bed file is required.";
  }
};

// currently only the sequence field is validated
const asyncValidate = async (values: any, dispatch: any, props: IBlastInputFormProps) => {
  if (values == null) {
    return;
  }
  try {
    // if an upload has failed then there is no need to continue validation
    // Testing Hint: Mimic a failed upload by removing the authorization header in method uploadFile of WebApiClient.ts
    if (anyFileHasStatus(props.fileUploads, FILE_STATUS.UPLOAD_FAILED)) {
      throw "One or more files failed to upload";
    }
  } catch (error) {
    throw {
      [COORD_LIST_UPLOAD_FORM_KEYS.FILE]:
        error instanceof Error || (error.message && typeof error.message === "string")
          ? "Could not validate the input: " + error.message
          : error || "An unexpected error occurred",
    };
  }

  return;
};

const CoordinatesListUploadFormDialog = reduxForm({
  form: COORD_LIST_UPLOAD_FORM_NAME,
  asyncValidate: asyncValidate,
})(UploadDialog) as any;

const CoordinatesListUploadManager: FC<Omit<UploadDialogFormProps, "fileUploads">> = (props) => {
  const dispatch = useDispatch<ReduxDispatch>();

  const fileUploads = useSelector<IAppState, Dictionary<IFileUpload>>(
    (state) => state.filesUploadState.filesUploadLookup,
  );

  // state cleanup
  useEffect(() => {
    return () => {
      dispatch(resetAllFileUploads());
    };
  }, []);

  // so that the redux-form async validation has access to fileUploads via the props !
  return <CoordinatesListUploadFormDialog {...props} fileUploads={fileUploads} />;
};
